import React, { useState } from "react";
import { get, post } from "../lib/api";
import { useLoad } from "../lib/hooks";
import { addDays, formatDay } from "../lib/format";
import { MCP_URL, claudeCodeCommand, claudeDesktopConfig, expiryInstant, scopeLabel } from "../lib/reports";
import { formatDate, todayISO } from "../../helpers/datetime";
import { Badge, Button, Card, Confirm, Empty, ErrorBox, Field, Input, Loading, Modal, Segment, Spinner } from "../ui";
import { useShell } from "../shell/context";

const STATUS_COLOR = { active: "success", revoked: "secondary", expired: "warning" };
const STATUS_LABEL = { active: "Active", revoked: "Revoked", expired: "Expired" };

export function CopyBlock({ text, label = "Copy", secret }) {
    const { toast } = useShell();
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            toast("Copied.");
        } catch (e) {
            toast("Copy failed: select the text and copy it by hand.", { error: true });
        }
    };
    return (
        <div className={"hz-copy" + (secret ? " secret" : "")}>
            <pre>{text}</pre>
            <Button size="sm" variant={secret ? "primary" : "outline"} icon="ri-file-copy-line" onClick={copy}>{label}</Button>
        </div>
    );
}

function ConnectSteps({ token }) {
    return (
        <div className="hz-form-grid">
            <div>
                <div className="hz-label">Claude Code</div>
                <div className="hz-small hz-muted" style={{ marginBottom: 6 }}>Run once in a terminal. Check with <code>claude mcp list</code> (horus ✓ Connected) or <code>/mcp</code> inside Claude Code.</div>
                <CopyBlock text={claudeCodeCommand(token)} />
            </div>
            <div>
                <div className="hz-label">Claude Desktop</div>
                <div className="hz-small hz-muted" style={{ marginBottom: 6 }}>
                    Settings → Developer → Edit Config (on macOS <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>), add this, then restart Claude Desktop. Needs Node.js 18+; the "Add custom connector" dialog does not work because Horus does not use OAuth.
                </div>
                <CopyBlock text={claudeDesktopConfig(token)} />
            </div>
            <div className="hz-small hz-muted">
                To change a token in Claude Code: <code>claude mcp remove horus --scope user</code>, then add it again. If a token leaks, revoke it here (it stops at its next request) and create a new one.
            </div>
        </div>
    );
}

function NewTokenModal({ open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [scope, setScope] = useState("read");
    const [expiry, setExpiry] = useState("never");
    const [date, setDate] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [created, setCreated] = useState(null);
    const today = todayISO();

    const close = () => {
        setName("");
        setScope("read");
        setExpiry("never");
        setDate("");
        setError(null);
        setCreated(null);
        setBusy(false);
        onClose();
    };

    const expiryDate = expiry === "never" ? null : expiry === "custom" ? date : addDays(today, Number(expiry));

    const create = async () => {
        if (!name.trim()) return setError("Give the token a name, e.g. Claude Code (laptop).");
        if (expiry === "custom" && (!date || date <= today)) return setError("Pick an expiry date after today.");
        setBusy(true);
        setError(null);
        try {
            const body = { name: name.trim(), scopes: scope === "write" ? ["read", "write"] : ["read"] };
            if (expiryDate) body.expires_at = expiryInstant(expiryDate);
            const res = await post("v1/settings/api-clients", body);
            setCreated(res);
            onCreated();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    if (created) {
        return (
            <Modal open={open} title="Token created" onClose={close} width={720} footer={<Button variant="primary" onClick={close}>Done</Button>}>
                <div className="hz-form-grid">
                    <div className="hz-alert hz-alert-warning">
                        <i className="ri-error-warning-line"></i> Copy the token now. It is shown only once; Horus keeps only a hash of it.
                    </div>
                    <div>
                        <div className="hz-label">{created.name} · {scopeLabel(created.scopes)}</div>
                        <CopyBlock text={created.token} label="Copy token" secret />
                    </div>
                    <ConnectSteps token={created.token} />
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            open={open}
            title="New Claude token"
            onClose={close}
            footer={
                <>
                    <Button variant="light" onClick={close}>Cancel</Button>
                    <Button variant="primary" disabled={busy} onClick={create}>{busy && <Spinner />}Create token</Button>
                </>
            }
        >
            <div className="hz-form-grid">
                {error && <div className="hz-alert hz-alert-danger">{error}</div>}
                <Field label="Name" required hint="One token per app and machine, so one can be revoked without the others.">
                    <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Claude Code (laptop)" onKeyDown={(e) => e.key === "Enter" && create()} />
                </Field>
                <div>
                    <span className="hz-label">Access</span>
                    <Segment
                        value={scope}
                        onChange={setScope}
                        options={[
                            { value: "read", label: "Read" },
                            { value: "write", label: "Read + write" },
                        ]}
                    />
                    <div className="hz-hint">
                        {scope === "read"
                            ? "Claude can search and read people, companies, leads, the due list and reports."
                            : "Claude can also log touches, create and update people, companies, leads and signals, import, merge and delete (to the trash). Changes show as made by Claude in History."}
                    </div>
                </div>
                <div>
                    <span className="hz-label">Expires</span>
                    <div className="hz-row-wrap" style={{ gap: 8 }}>
                        <Segment
                            value={expiry}
                            onChange={setExpiry}
                            options={[
                                { value: "never", label: "Never" },
                                { value: "30", label: "30 days" },
                                { value: "90", label: "90 days" },
                                { value: "custom", label: "Date" },
                            ]}
                        />
                        {expiry === "custom" && <Input small type="date" min={addDays(today, 1)} value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 150 }} />}
                    </div>
                    {expiryDate && <div className="hz-hint">Stops working at the end of {formatDay(expiryDate)} (Istanbul time).</div>}
                </div>
            </div>
        </Modal>
    );
}

// Settings → Claude access: MCP client tokens (E10).
export default function ClaudeAccess() {
    const { toast } = useShell();
    const { data, error, loading, reload } = useLoad(() => get("v1/settings/api-clients"), []);
    const [creating, setCreating] = useState(false);
    const [revoking, setRevoking] = useState(null);
    const [busy, setBusy] = useState(false);
    const rows = (data && data.data) || [];

    const revoke = async () => {
        setBusy(true);
        try {
            await post(`v1/settings/api-clients/${revoking.id}/revoke`);
            toast(`${revoking.name} revoked.`);
            setRevoking(null);
            reload({ silent: true });
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="hz-stack">
            <Card
                noBody
                title="Claude access"
                actions={<Button variant="primary" icon="ri-add-line" onClick={() => setCreating(true)}>New token</Button>}
            >
                <div className="hz-card-section hz-small hz-muted">
                    Claude Code and Claude Desktop reach Horus through its MCP server at <code>{MCP_URL}</code>, each with its own token.
                </div>
                <ErrorBox error={error} onRetry={reload} />
                {loading && !data && <Loading />}
                {data && rows.length === 0 && <Empty>No tokens yet.</Empty>}
                {rows.length > 0 && (
                    <div className="hz-table-wrap">
                        <table className="hz-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Access</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Last used</th>
                                    <th>Expires</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => (
                                    <tr key={c.id} className={c.status !== "active" ? "hz-row-dim" : ""}>
                                        <td>
                                            <span className="hz-strong">{c.name}</span>
                                            <span className="hz-entity-sub"><code>{c.token_prefix}…</code></span>
                                        </td>
                                        <td>
                                            <Badge color={(c.scopes || []).includes("write") ? "warning" : "info"}>{scopeLabel(c.scopes)}</Badge>
                                        </td>
                                        <td>
                                            <Badge color={STATUS_COLOR[c.status] || "secondary"}>{STATUS_LABEL[c.status] || c.status}</Badge>
                                            {c.revoked_at && <span className="hz-entity-sub">{formatDate(c.revoked_at)}</span>}
                                        </td>
                                        <td className="hz-nowrap">{formatDate(c.created_at)}</td>
                                        <td className="hz-nowrap">{c.last_used_at ? formatDate(c.last_used_at) : <span className="hz-muted">Never</span>}</td>
                                        <td className="hz-nowrap">{c.expires_at ? formatDate(c.expires_at) : <span className="hz-muted">Never</span>}</td>
                                        <td className="actions">
                                            {c.status === "active" && (
                                                <Button size="sm" variant="outline" onClick={() => setRevoking(c)}>Revoke</Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
            <Card title="How to connect">
                <ConnectSteps token="hrs_…" />
                <div className="hz-small hz-muted" style={{ marginTop: 12 }}>Replace <code>hrs_…</code> with a token. A new token's commands come filled in.</div>
            </Card>
            <NewTokenModal open={creating} onClose={() => setCreating(false)} onCreated={() => reload({ silent: true })} />
            <Confirm open={!!revoking} title="Revoke token" danger confirmLabel="Revoke" busy={busy} onConfirm={revoke} onClose={() => setRevoking(null)}>
                {revoking && (
                    <>
                        <strong>{revoking.name}</strong> stops working at its next request. Claude apps using it lose access to Horus until they get a new token. This cannot be undone.
                    </>
                )}
            </Confirm>
        </div>
    );
}
